import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { addDoc, collection, serverTimestamp, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { LoanStatuses } from '../constants/statuses';

const ApplicationTypeSelect = ({ brokerId }) => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!selectedType || !businessName || !email || !phoneNumber) {
      alert('Please fill in all fields and select a contact type');
      return;
    }

    setIsSubmitting(true);
    try {
      const now = serverTimestamp();
      const newApplication = {
        businessName,
        emails: [email],
        phoneNumbers: [phoneNumber],
        createdAt: now,
        lastUpdated: now,
        lastUserUpdate: now,
        loanPurpose: "workingCapital",
        loanStatus: LoanStatuses.NEW_APPLICATION,
        userId: brokerId,
        weContact: selectedType === 'weContact'
      };

      const docRef = await addDoc(collection(db, 'loanApplications'), newApplication);
      
      await updateDoc(doc(db, 'loanApplications', docRef.id), {
        id: docRef.id
      });

      const brokerRef = doc(db, 'brokers', brokerId);
      await updateDoc(brokerRef, {
        loanApplications: arrayUnion(docRef.id)
      });
      
      navigate(`/shared-upload/${docRef.id}`);
    } catch (error) {
      console.error('Error creating application:', error);
      alert('There was an error creating the application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold mb-8 text-center">Choose Application Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* You contact client option */}
          <button
            onClick={() => setSelectedType('youContact')}
            className={`relative block w-full rounded-lg border-2 ${
              selectedType === 'youContact' 
                ? 'border-indigo-500 ring-2 ring-indigo-500' 
                : 'border-dashed border-gray-300'
            } p-12 text-center hover:border-gray-400 focus:outline-none`}
          >
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-lg font-semibold text-gray-900">
              You Contact Client
            </span>
            <span className="mt-2 block text-sm text-gray-600">
              You'll handle all communication with the client
            </span>
          </button>

          {/* We contact client option */}
          <button
            onClick={() => setSelectedType('weContact')}
            className={`relative block w-full rounded-lg border-2 ${
              selectedType === 'weContact' 
                ? 'border-indigo-500 ring-2 ring-indigo-500' 
                : 'border-dashed border-gray-300'
            } p-12 text-center hover:border-gray-400 focus:outline-none`}
          >
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-lg font-semibold text-gray-900">
              We Contact Client
            </span>
            <span className="mt-2 block text-sm text-gray-600">
              We'll handle all communication with the client
            </span>
          </button>
        </div>

        {/* Form Section */}
        <div className="mt-8 max-w-md mx-auto">
          <form onSubmit={handleContinue} className="space-y-4">
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                Business Name
              </label>
              <input
                type="text"
                id="businessName"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>

            <button
              type="submit"
              disabled={!selectedType || isSubmitting}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationTypeSelect;